import {
  Box,
  Card,
  CardMedia,
  Typography,
  Avatar,
  Chip,
  Fade,
  Stack,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { CampaignOutlined, NewReleasesOutlined } from "@mui/icons-material";
import { convertUTCToLocal } from "@/lib/utils/dateUtils";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

// Check if news is new (created within last 24 hours)
const isNewAnnouncement = (createdAt) => {
  const created = new Date(createdAt);
  const now = new Date();
  const diffHours = (now - created) / (1000 * 60 * 60);
  return diffHours < 24;
};

function AnnouncementCard({ announcement, onReadMore }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const preview =
    announcement.body.length > 120
      ? `${announcement.body.slice(0, 120)}...`
      : announcement.body;

  // Find the first image attachment (e.g., JPEG/PNG)
  const previewImage = announcement.attachments?.find(
    (att) => att.is_image && att.url
  );

  return (
    <Fade in timeout={600}>
      <Box
        sx={{
          position: "relative",
          p: 1, // Padding creates stable hover zone
          m: -0.5, // Negative margin to maintain spacing
          borderRadius: 4,
          "&:hover .card-container": {
            transform: "translateY(-4px)",
            boxShadow: theme.shadows[8],
            borderColor: "primary.main",
            "& .news-card-image": {
              transform: "scale(1.02)",
            },
          },
        }}
      >
        <Card
          elevation={2}
          className="card-container"
          sx={{
            p: { xs: 2, sm: 3 },
            height: { xs: 180, sm: 200, md: 220 },
            display: "flex",
            flexDirection: "row",
            position: "relative",
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            cursor: "pointer",
            willChange: "transform, box-shadow",
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
          }}
          onClick={onReadMore}
          aria-label={t("news.cardAria", { title: announcement.title })}
        >
          {/* Image Preview on Left */}
          {previewImage ? (
            <CardMedia
              component="img"
              image={previewImage.url}
              alt={t("news.imageAlt", { name: previewImage.display_name })}
              className="news-card-image"
              sx={{
                width: { xs: 100, sm: 120, md: 150 },
                height: "100%",
                objectFit: "cover",
                borderRadius: 2,
                transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                willChange: "transform",
              }}
            />
          ) : (
            <Box
              className="news-card-image"
              sx={{
                width: { xs: 100, sm: 120, md: 150 },
                height: "100%",
                backgroundColor: alpha(theme.palette.background.default, 0.5),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontStyle: "italic",
                color: "text.secondary",
                borderRadius: 2,
                border: "2px dashed",
                borderColor: "divider",
                transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                willChange: "transform",
              }}
            >
              <Stack alignItems="center" spacing={1}>
                <CampaignOutlined
                  sx={{
                    fontSize: { xs: 24, sm: 28, md: 32 },
                    color: "text.disabled",
                  }}
                />
                <Typography
                  variant="caption"
                  textAlign="center"
                  sx={{
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  {t("news.noImageText")}
                </Typography>
              </Stack>
            </Box>
          )}

          {/* Content on Right */}
          <Box sx={{ flex: 1, p: { xs: 1, sm: 2 }, overflow: "hidden" }}>
            <Box display="flex" alignItems="center" mb={1}>
              <Avatar
                src={announcement.author.avatar_url}
                alt={t("news.authorAvatarAlt", {
                  name: announcement.author.first_name,
                })}
                sx={{
                  width: { xs: 28, sm: 32, md: 36 },
                  height: { xs: 28, sm: 32, md: 36 },
                  mr: 1,
                }}
                aria-label={t("news.authorAvatarAria")}
              />
              <Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    noWrap
                    sx={{
                      maxWidth: { xs: 120, sm: 140, md: 160 },
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    }}
                  >
                    {announcement.author.first_name || t("news.unknownAuthor")}{" "}
                    {announcement.author.last_name || t("news.unknownAuthor")}
                  </Typography>
                  {isNewAnnouncement(announcement.created_at) && (
                    <Chip
                      icon={
                        <NewReleasesOutlined
                          sx={{ fontSize: { xs: 12, sm: 14 } }}
                        />
                      }
                      label={t("news.newChipLabel")}
                      size="small"
                      sx={{
                        backgroundColor: alpha(
                          theme.palette.success.main,
                          0.15
                        ),
                        color: theme.palette.success.dark,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                        fontWeight: 600,
                        "& .MuiChip-icon": {
                          color: theme.palette.success.dark,
                        },
                        height: { xs: 16, sm: 20 },
                        "& .MuiChip-label": {
                          px: { xs: 1, sm: 2 },
                          fontSize: { xs: "0.625rem", sm: "0.75rem" },
                        },
                      }}
                      aria-label={t("news.newChipAria")}
                    />
                  )}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: "0.625rem", sm: "0.75rem" },
                  }}
                >
                  {convertUTCToLocal(announcement.created_at) ||
                    t("news.unknownDate")}
                </Typography>
              </Box>
            </Box>

            <Typography
              variant="subtitle1"
              fontWeight={600}
              noWrap
              sx={{
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              {announcement.title || t("news.unknownTitle")}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.4,
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
              }}
            >
              {preview || t("news.unknownBody")}
            </Typography>
          </Box>
        </Card>
      </Box>
    </Fade>
  );
}

AnnouncementCard.propTypes = {
  announcement: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    body: PropTypes.string,
    created_at: PropTypes.string,
    author: PropTypes.shape({
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      avatar_url: PropTypes.string,
    }),
    attachments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        url: PropTypes.string,
        is_image: PropTypes.bool,
        display_name: PropTypes.string,
      })
    ),
  }).isRequired,
  onReadMore: PropTypes.func.isRequired,
};

export default AnnouncementCard;
